import { GrowthBook } from '@growthbook/growthbook-react'
import axios, { AxiosError, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import { useEffect, useState } from 'react'
import { emitExperimentViewedGAEvent } from '../ga-event-helpers'

// AM-123 (H3): module-level dedup so experiment_viewed fires at most once per
// (experiment, variation) across the whole page lifetime. A fresh GrowthBook
// instance is created per Header mount, so its per-instance trackingCallback
// dedup was wiped on every SPA navigation and the event re-fired each time.
const emittedExperiments = new Set<string>()

axiosRetry(axios, {
	retries: 3,
	retryDelay: (retryCount) => {
		// console.log(`retry attempt: ${retryCount}`)
		return retryCount * 2000
	},
})

export const useGrowthbook = (isMainPage: boolean) => {
	const experimentEndpoint = `${process.env.NEXT_PUBLIC_BACKEND_URL}/hotels/experiment?api-key=${process.env.NEXT_PUBLIC_API_KEY}`
	const [growthbook, setGrowtBookInstance] = useState<
		GrowthBook<Record<string, any>>
	>(
		new GrowthBook({
			enableDevMode: process.env.NODE_ENV !== 'production',
			apiHost: 'https://cdn.growthbook.io/',
			clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_API_KEY,
			trackingCallback: (experiment, result) => {
				// console.log('Tracking callback called')

				const interval = setInterval(() => {
					if (!window.dataLayer) return
					// Clear as soon as the dataLayer exists (fixes the old leak where the
					// non-main-page skip path returned without clearing the interval).
					clearInterval(interval)

					// AM-123 (D2): the sign-up experiment key is 'bhp-sign-up'
					// (cf. useFeatureIsOn('bhp-sign-up') in Booking.tsx). The old guard
					// checked 'bhb-sign-up' and never matched, so this event was not
					// suppressed on non-main pages as intended.
					if (experiment.key === 'bhp-sign-up' && !isMainPage) {
						return
					}

					// AM-123 (H3): dedupe across SPA navigations.
					const dedupeKey = `${experiment.key}:${result.variationId}`
					if (emittedExperiments.has(dedupeKey)) return
					emittedExperiments.add(dedupeKey)

					emitExperimentViewedGAEvent(
						experiment.key,
						result.variationId.toString(),
						new Date().toISOString(),
					)
				}, 2000)
				
				axios
					.post(
						experimentEndpoint,
						{
							experiment_id: experiment.key,
							variation_id: result.variationId.toString(),
							event_timestamp: new Date().toISOString(),
						},
						{
							withCredentials: true,
						}
					)
					.then((response: AxiosResponse) => {
						// console.log(JSON.stringify('experiment viewed', response.data))
					})
					.catch((error: AxiosError) => {
						console.log(error)
					})
			},
		})
	)

	useEffect(() => {
		if (growthbook) {
			growthbook.setAttributes({
				url: window.location.href,
			})
			growthbook.init()
		}
	}, [growthbook])

	const gbCallback = (params: { uid: string }) => {
		const { uid } = params

		growthbook.setAttributes({
			id: uid,
			url: window.location.href,
		})
	}
	return { growthbook, gbCallback }
}
